import { useContext, useState } from "react";
import { Nav } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import {
  Grid,
  FileEarmarkText,
  CloudUpload,
  BarChart,
  Book,
  Key,
  PersonCircle,
  CurrencyDollar,
  Lightning,
  List as MenuIcon,
} from "react-bootstrap-icons";

import { NavSection } from "./NavSection";
import { NavItem } from "./NavItem";
import { UserGuide } from "./UserGuide";
import logo from "../../static/images/ClearPoint_Trust_Logo.jpg";
import "./styles/NavSidebar.css";
import { SignOut } from "./Signout";
import { EnhancementModal } from "../widgets/components/modals";
import { NavUserCard } from "./NavUserCard";
import { AuthContext } from "../../lib/contexts/AuthContext";

export function NavSidebar({ user, collapsed: controlledCollapsed, onToggle }) {
  const { hasClaim } = useContext(AuthContext);

  const MAIN_ITEMS = [
    { to: "/dashboard", label: "Dashboard", icon: Grid },
    { to: "/contracts", label: "Contracts", icon: FileEarmarkText },
    { to: "/document-upload", label: "Document Upload", icon: CloudUpload },
    {
      to: "/reports",
      label: "Reports",
      icon: BarChart,
      children: [
        { to: "/reports", label: "Reports" },
        // If they have onDemandReports claim, show the sub-item, otherwise hide it
        ...(hasClaim("OnDemandReports")
          ? [{ to: "/on-demand-report-request", label: "On-Demand Request" }]
          : []),
      ],
    },
    { to: "/library", label: "Library", icon: Book },
    {
      href: "https://clientpoint.fisglobal.com/tdcb/main/UserLogon?bankNumber=r9&subProduct",
      external: true,
      label: "Account Access",
      icon: Key,
    },
    { to: "/customer/profile", label: "Customer Profile", icon: PersonCircle },

    // Conditional root item for Commissions
    ...(hasClaim("Commissions")
      ? [
          {
            to: "/commissions",
            label: "Commissions",
            icon: CurrencyDollar,
            children: [
              { to: "/commissions-contracts", label: "Contract Approval" },
              { to: "/commissions-payout-runs", label: "Payout Runs" },
            ],
          },
        ]
      : []),
  ];

  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [showEnhancementModal, setShowEnhancementModal] = useState(false);

  const collapsed =
    controlledCollapsed != null ? controlledCollapsed : internalCollapsed;

  const handleToggle = () => {
    const next = !collapsed;
    if (onToggle) onToggle(next);
    else setInternalCollapsed(next);
  };

  const location = useLocation();
  const isActive = (to) => to && location.pathname === to;

  const handleOpenEnhancement = (e) => {
    e.preventDefault();
    setShowEnhancementModal(true);
  };

  const handleCloseEnhancement = () => {
    setShowEnhancementModal(false);
  };

  const scrollPageTop = () => {
    // Some pages scroll the window while others scroll the app shell container.
    document.querySelector(".cp-shell__container")?.scrollTo(0, 0);
    document.querySelector(".cp-shell__main")?.scrollTo(0, 0);
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const handleRouteClick = (to) => {
    if (to === "/commissions-contracts" || to === "/commissions-payout-runs") {
      requestAnimationFrame(scrollPageTop);
    }
  };

  const BOTTOM_ITEMS = [
    {
      label: "Enhancement Request",
      icon: Lightning,
      onClick: handleOpenEnhancement,
      href: "#enhancement",
    },
  ];

  return (
    <>
      <aside
        className={"cp-nav" + (collapsed ? " cp-nav--collapsed" : "")}
        aria-label="Main navigation"
      >
        <div className="cp-nav__brand">
          {!collapsed && (
            <Link to="/">
              <img src={logo} alt="ClearPoint Trust" className="cp-nav__logo" />
            </Link>
          )}
          <button
            type="button"
            className="cp-nav__toggle"
            onClick={handleToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <MenuIcon />
          </button>
        </div>

        {/* User Card placed explicitly below logo row and above main scrolling viewport links */}
        <NavUserCard collapsed={collapsed} />

        <Nav as="nav" className="cp-nav__main" aria-label="Primary">
          <NavSection label={!collapsed ? "Navigation" : null}>
            {MAIN_ITEMS.map((item) => (
              <NavItem
                key={item.to || item.href}
                to={item.to}
                href={item.href}
                external={item.external}
                onRouteClick={handleRouteClick}
                label={item.label}
                icon={item.icon}
                badge={item.badge}
                children={item.children}
                active={isActive(item.to)}
                isActive={isActive}
                collapsed={collapsed}
              />
            ))}
          </NavSection>
        </Nav>

        <Nav as="nav" className="cp-nav__footer" aria-label="Account">
          <NavSection>
            {/* UserGuide self-injects `api` via WithApiAccess HOC */}
            <UserGuide collapsed={collapsed} />

            {BOTTOM_ITEMS.map((item, index) => (
              <NavItem
                key={item.to || item.href || `bottom-item-${index}`}
                to={item.to}
                href={item.href}
                onClick={item.onClick}
                onRouteClick={handleRouteClick}
                external={item.external}
                label={item.label}
                icon={item.icon}
                active={isActive(item.to)}
                collapsed={collapsed}
              />
            ))}
            <SignOut />
          </NavSection>
        </Nav>
      </aside>

      {/* Enhancement Request Target Form Overlay Viewport */}
      <EnhancementModal
        show={showEnhancementModal}
        onHide={handleCloseEnhancement}
      />
    </>
  );
}
